import { useState } from 'react'

import type { DashboardMonthPoint, DashboardTrend } from '@/features/admin/api/dashboard.api'
import {
  HiringFunnel,
  InsightTiles,
  ProgramPerformance,
  RecentHires,
  SkillsDemandSupply,
  TopEmployers,
} from '@/features/admin/components/AdminDashboardInsights'
import { useAdminDashboardQuery } from '@/features/admin/hooks/useAdminDashboard'

// ─── Trend helpers ───────────────────────────────────────────────────────────

function trendPct(trend: DashboardTrend): number | null {
  if (trend.previous === 0) return trend.current > 0 ? 100 : null
  return Math.round(((trend.current - trend.previous) / trend.previous) * 1000) / 10
}

function TrendBadge({ trend }: { trend: DashboardTrend }) {
  const pct = trendPct(trend)
  if (pct === null) return <span className="fs-12 text-muted">No activity in the previous 30 days</span>
  const up = pct >= 0
  return (
    <span className={`fs-12 ${up ? 'text-success' : 'text-danger'}`}>
      {up ? 'Increased by' : 'Decreased by'}&nbsp;
      <i className={`ti ${up ? 'ti-trending-up' : 'ti-trending-down'} me-1`} />
      <strong>{Math.abs(pct)}%</strong>
    </span>
  )
}

// ─── Stat tile ───────────────────────────────────────────────────────────────

interface TileProps {
  label: string
  value: number | string
  trend: DashboardTrend
  iconClass: string
  iconBg: string
}

function StatTile({ label, value, trend, iconClass, iconBg }: TileProps) {
  return (
    <div className="col-xl-3 col-md-6">
      <div className="card custom-card">
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <p className="fs-13 text-muted mb-1">{label}</p>
              <p className="fs-28 fw-bold mb-2 lh-1">{value.toLocaleString()}</p>
              <TrendBadge trend={trend} />
            </div>
            <div
              className={`rounded-3 d-flex align-items-center justify-content-center ${iconBg}`}
              style={{ width: 52, height: 52, flexShrink: 0 }}
            >
              <i className={`ti ${iconClass} fs-22`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── SVG line chart ──────────────────────────────────────────────────────────

const CHART_W = 900
const CHART_H = 260
const PAD = { top: 20, right: 20, bottom: 40, left: 50 }
const INNER_W = CHART_W - PAD.left - PAD.right
const INNER_H = CHART_H - PAD.top - PAD.bottom

function shortMonth(ym: string) {
  const parts = ym.split('-')
  const y = parts[0] ?? ''
  const m = parts[1] ?? '1'
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${months[parseInt(m, 10) - 1] ?? m} '${y.slice(2)}`
}

function svgPath(points: [number, number][]): string {
  if (points.length < 2) return ''
  // Catmull-Rom → cubic bezier approximation for smooth curves
  // Array accesses are safe: loop bounds + Math.max/min guarantee valid indices.
  const first = points[0]!
  const result: string[] = [`M ${first[0]} ${first[1]}`]
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)]!
    const p1 = points[i]!
    const p2 = points[i + 1]!
    const p3 = points[Math.min(i + 2, points.length - 1)]!
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6
    result.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`)
  }
  return result.join(' ')
}

const TOOLTIP_W = 160
const TOOLTIP_H = 72

function LineChart({ data }: { data: DashboardMonthPoint[] }) {
  const [hovered, setHovered] = useState<number | null>(null)

  if (!data.length) return null

  const appMax = Math.max(...data.map((d) => d.applications), 1)
  const hirMax = Math.max(...data.map((d) => d.hirings), 1)
  const yMax = Math.max(appMax, hirMax, 10)

  const step = Math.ceil(yMax / 5)
  const niceMax = step * 5
  const ticks = Array.from({ length: 6 }, (_, i) => i * step)

  const xOf = (i: number) => PAD.left + (i / (data.length - 1)) * INNER_W
  const yOf = (v: number) => PAD.top + INNER_H - (v / niceMax) * INNER_H

  const appPts = data.map((d, i): [number, number] => [xOf(i), yOf(d.applications)])
  const hirPts = data.map((d, i): [number, number] => [xOf(i), yOf(d.hirings)])

  const hov = hovered !== null ? data[hovered] : null
  const hovX = hovered !== null ? xOf(hovered) : 0
  // Keep tooltip inside chart bounds
  const tipX = hovered !== null
    ? (hovX + TOOLTIP_W + 16 > CHART_W - PAD.right ? hovX - TOOLTIP_W - 12 : hovX + 12)
    : 0
  const tipY = PAD.top

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      aria-label="Monthly hirings vs applications"
      onMouseLeave={() => setHovered(null)}
    >
      {/* Y-axis grid lines + labels */}
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.left} y1={yOf(t)} x2={CHART_W - PAD.right} y2={yOf(t)} stroke="#e5e7eb" strokeWidth={1} />
          <text x={PAD.left - 8} y={yOf(t) + 4} textAnchor="end" fontSize={11} fill="#9ca3af">{t}</text>
        </g>
      ))}

      {/* X-axis labels */}
      {data.map((d, i) => (
        <text key={d.month} x={xOf(i)} y={CHART_H - 6} textAnchor="middle" fontSize={11} fill="#9ca3af">
          {shortMonth(d.month)}
        </text>
      ))}

      {/* Applications line */}
      <path d={svgPath(appPts)} fill="none" stroke="#6366f1" strokeWidth={2.5} strokeLinejoin="round" />

      {/* Hirings line */}
      <path d={svgPath(hirPts)} fill="none" stroke="#f97316" strokeWidth={2.5} strokeDasharray="6 4" strokeLinejoin="round" />

      {/* Dots — applications */}
      {appPts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={hovered === i ? 5.5 : 3.5} fill="#6366f1" stroke="#fff" strokeWidth={hovered === i ? 2 : 0} style={{ transition: 'r 0.1s' }} />
      ))}

      {/* Dots — hirings */}
      {hirPts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={hovered === i ? 5.5 : 3.5} fill="#f97316" stroke="#fff" strokeWidth={hovered === i ? 2 : 0} style={{ transition: 'r 0.1s' }} />
      ))}

      {/* Hover crosshair */}
      {hovered !== null && (
        <line x1={hovX} y1={PAD.top} x2={hovX} y2={PAD.top + INNER_H} stroke="#9ca3af" strokeWidth={1} strokeDasharray="4 3" />
      )}

      {/* Invisible hit-area columns for hover detection */}
      {data.map((_, i) => {
        const x = xOf(i)
        const colW = INNER_W / (data.length - 1)
        return (
          <rect
            key={i}
            x={x - colW / 2}
            y={PAD.top}
            width={colW}
            height={INNER_H}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
            onMouseEnter={() => setHovered(i)}
          />
        )
      })}

      {/* Tooltip box */}
      {hov != null && hovered !== null && (
        <g>
          <rect x={tipX} y={tipY} width={TOOLTIP_W} height={TOOLTIP_H} rx={6} ry={6} fill="#1e293b" opacity={0.92} />
          {/* Month label */}
          <text x={tipX + 12} y={tipY + 20} fontSize={12} fill="#94a3b8" fontWeight="600">
            {shortMonth(hov.month)}
          </text>
          {/* Applications row */}
          <circle cx={tipX + 12} cy={tipY + 38} r={5} fill="#6366f1" />
          <text x={tipX + 24} y={tipY + 42} fontSize={12} fill="#e2e8f0">Applications</text>
          <text x={tipX + TOOLTIP_W - 10} y={tipY + 42} fontSize={12} fill="#e2e8f0" textAnchor="end" fontWeight="700">
            {hov.applications}
          </text>
          {/* Hirings row */}
          <circle cx={tipX + 12} cy={tipY + 58} r={5} fill="#f97316" />
          <text x={tipX + 24} y={tipY + 62} fontSize={12} fill="#e2e8f0">Hirings</text>
          <text x={tipX + TOOLTIP_W - 10} y={tipY + 62} fontSize={12} fill="#e2e8f0" textAnchor="end" fontWeight="700">
            {hov.hirings}
          </text>
        </g>
      )}
    </svg>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminDashboardPage() {
  const { data, isPending } = useAdminDashboardQuery()

  const EMPTY_TREND: DashboardTrend = { current: 0, previous: 0 }

  const tiles = [
    {
      label: 'Total Organisations',
      value: data?.totals.organizations ?? 0,
      trend: data?.trends.organizations ?? EMPTY_TREND,
      iconClass: 'ti-building',
      iconBg: 'bg-primary-transparent text-primary',
    },
    {
      label: 'Total Jobs Posted',
      value: data?.totals.jobs ?? 0,
      trend: data?.trends.jobs ?? EMPTY_TREND,
      iconClass: 'ti-briefcase',
      iconBg: 'bg-info-transparent text-info',
    },
    {
      label: 'Total Applications',
      value: data?.totals.applications ?? 0,
      trend: data?.trends.applications ?? EMPTY_TREND,
      iconClass: 'ti-file-text',
      iconBg: 'bg-success-transparent text-success',
    },
    {
      label: 'Total Hirings',
      value: data?.totals.hirings ?? 0,
      trend: data?.trends.hirings ?? EMPTY_TREND,
      iconClass: 'ti-user-check',
      iconBg: 'bg-warning-transparent text-warning',
    },
  ]

  return (
    <>
      <div className="my-4 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-0">Dashboard</p>
        <p className="text-muted fs-13 mb-0">Platform overview — last 30 days compared with the 30 days before.</p>
      </div>

      {/* Stat tiles */}
      {isPending ? (
        <div className="d-flex gap-3 mb-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card custom-card flex-fill" style={{ minHeight: 110 }}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm text-muted" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-4">
          {tiles.map((tile) => (
            <StatTile key={tile.label} {...tile} />
          ))}
        </div>
      )}

      {data && <InsightTiles data={data} />}

      <div className="row g-3 mb-4">
        <div className="col-xl-8">
      {/* Monthly chart */}
      <div className="card custom-card h-100">
        <div className="card-header border-bottom d-flex align-items-center justify-content-between">
          <p className="fw-semibold fs-15 mb-0">Statistics</p>
          {/* Legend */}
          <div className="d-flex align-items-center gap-3 fs-12 text-muted">
            <span className="d-flex align-items-center gap-1">
              <svg width="24" height="4"><line x1="0" y1="2" x2="24" y2="2" stroke="#6366f1" strokeWidth="2.5" /></svg>
              Applications
            </span>
            <span className="d-flex align-items-center gap-1">
              <svg width="24" height="4">
                <line x1="0" y1="2" x2="24" y2="2" stroke="#f97316" strokeWidth="2.5" strokeDasharray="5 3" />
              </svg>
              Hirings
            </span>
          </div>
        </div>
        <div className="card-body p-3">
          {isPending ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 240 }}>
              <span className="spinner-border text-muted" />
            </div>
          ) : (
            <LineChart data={data?.monthlyChart ?? []} />
          )}
        </div>
      </div>
        </div>
        <div className="col-xl-4">{data && <HiringFunnel stages={data.hiringFunnel} />}</div>
      </div>

      {data && (
        <>
          <div className="row g-3 mb-4">
            <div className="col-xl-7">
              <SkillsDemandSupply rows={data.skillsDemandSupply} />
            </div>
            <div className="col-xl-5">
              <RecentHires hires={data.recentHires} />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-xl-6">
              <TopEmployers rows={data.topEmployers} />
            </div>
            <div className="col-xl-6">
              <ProgramPerformance rows={data.programPerformance} />
            </div>
          </div>
        </>
      )}
    </>
  )
}
